import { EventEmitter, Injectable } from '@angular/core';
import { LeaveOperations } from 'app/modules/common/enums/leave-operations';
import {
    EmployeeLeaveModel,
    LeaveModel,
    NewLeaveModel,
} from '../entities/NewLeaveModel';
import { Observable, Subject } from 'rxjs';
import { RestResponse } from 'app/modules/common/entities/rest.response.model';
import { ApiService } from 'app/modules/common/services/api.service';
import API from '../../common/services/api.config.json';
import {
    UntypedFormGroup,
    UntypedFormBuilder,
    Validators,
    UntypedFormControl,
} from '@angular/forms';
import { LeaveValidatorService } from './leave-validator.service';
import {
    maxLengthValidator,
    LessThan80YearsValidator,
    requiredValidator,
    NoFutureDateValidator,
    NineCharacterValidation,
    noWhiteSpacesValidator,
} from 'app/modules/common/validation/common-validation';
import moment, { Moment } from 'moment';
import { DateHelper } from 'app/modules/common/helpers/DateHelper';
import { LeaveService } from 'app/modules/leave/services/leave.service';
import { TaskManagementService } from 'app/modules/task/services/task-management.service';
import Utils from 'app/Utils';

/**
 * A validator to be run at leave creation. The validator should return null
 * if there is no error, otherwise the error message
 */
type LeaveCreationValidator = (form: UntypedFormGroup) => string | Promise<string>;

@Injectable({
    providedIn: 'root',
})
export class CreateLeaveService {
    public leaveOperation: LeaveOperations;
    public employeeId: number;

    public newLeaveModel: NewLeaveModel;
    public leaveDoctrId = new Subject<number>();
    public isOpenLeave = true;
    public isManagedLeave = false;
    public isLeaveInTiposBaja = false;
    public isLeaveInTiposSinGestion = true;
    public leaveRef: string;
    public employeeFormGroup: UntypedFormGroup;
    public leaveForm: UntypedFormGroup;

    public formsInitalized = new EventEmitter(false);
    public employeeLoaded = new EventEmitter(false);

    // validators to run on creation of leaves
    private leaveCreationValidators: LeaveCreationValidator[] = [];

    constructor(
        private apiService: ApiService,
        private leaveValidatorService: LeaveValidatorService,
        private formBuilder: UntypedFormBuilder,
        private leaveService: LeaveService,
        private taskManagementService: TaskManagementService
    ) {
        // add leave creation validators
        this.leaveCreationValidators.push(
            this.leaveDischargeDateGTLeaveDateValidator
        );
    }

    public initNewLeaveForms(): void {
        this.employeeFormGroup = this.formBuilder.group({
            EmployeeId: [0, Validators.required],
            Name: ['', [Validators.required]],
            Surname: ['', [Validators.required]],
            EmployeeRef: ['', [maxLengthValidator(20)]],
            Nic: ['', [maxLengthValidator(10)]],
            Ss: ['', [maxLengthValidator(15)]],
            HiringDate: [null],
            ContractEndDate: [null],
            Gender: [''],
            BirthDate: [null, [LessThan80YearsValidator()]],
            Age: [''],
            Urgent: [false],
            WorkingHourPercent: [''],
            Position: [''],
            CompanyId: [0],
            Directions: this.formBuilder.group({
                Telephone1: [
                    '',
                    [
                        NineCharacterValidation(),
                        noWhiteSpacesValidator(),
                        Validators.pattern('[0-9]*'),
                    ],
                ],
                Telephone2: [
                    '',
                    [
                        NineCharacterValidation(),
                        noWhiteSpacesValidator(),
                        Validators.pattern('[0-9]*'),
                    ],
                ],
                Movil: [
                    '',
                    [
                        NineCharacterValidation(),
                        noWhiteSpacesValidator(),
                        Validators.pattern('[0-9]*'),
                    ],
                ],
                Telephone4: [
                    '',
                    [
                        NineCharacterValidation(),
                        noWhiteSpacesValidator(),
                        Validators.pattern('[0-9]*'),
                    ],
                ],
                Direction: [''],
                PostalCode: [''],
                Province: [''],
                City: [''],
                Direction2: [''],
                PostalCode2: [''],
                Province2: [''],
                City2: [''],
                Observation: [''],
            }),
        });

        this.leaveForm = this.formBuilder.group(
            {
                leaveId: [0],
                leaveDate: [
                    moment(),
                    [
                        requiredValidator(),
                        NoFutureDateValidator(),
                        this.leaveValidatorService.sameLeaveDateValidation(),
                        this.leaveValidatorService.openLeaveOverlapValidator(),
                        this.leaveValidatorService.leaveOverlapValidator(),
                    ],
                ],
                dischargeDate: [
                    null,
                    [
                        NoFutureDateValidator(),
                        this.leaveValidatorService.leaveDischargeOverlapValidator(),
                        this.leaveValidatorService.communicationResetValidator(),
                    ],
                ],
                leaveCommunication: [
                    moment(),
                    [
                        NoFutureDateValidator(),

                        this.leaveValidatorService.leaveCommunicationRequiredValidator(),
                    ],
                ],
                dischargeCommunication: [
                    null,
                    [
                        NoFutureDateValidator(),
                        this.leaveValidatorService.dischargedDateAndDischargedCommunicationDateValidation(),
                    ],
                ],
                leaveType: ['E', Validators.required],
                relapse: [false],
                noPhone: [false],
                noAddress: [false],
                accidentPlace: [''],
                accidentLocation: [''],
                leaveRef: [''],
                medicoId: [null],
                comments: [null],
            },
            {
                validators: [
                    this.leaveValidatorService.communicationDaysValidator(),
                    this.leaveValidatorService.isLeaveDateAfterLastDischargeValidation(),
                    this.leaveValidatorService.dischargedCommunicationDateAndDischargedDateValidation(),
                    this.leaveValidatorService.leaveDateAndCommunicationDateValidation(),
                ],
            }
        );

        this.leaveValidatorService.validationForm = this.leaveForm;
        this.formsInitalized.next(true);
    }

    private mapNewLeaveFormToModel(): void {
        this.newLeaveModel = new NewLeaveModel();
        this.newLeaveModel.Employee = this.mapEmployeeModel();
        this.newLeaveModel.NewLeave = this.mapLeaveModel();
        this.newLeaveModel.NextTask =
            this.isOpenLeave && this.isManagedLeave
                ? this.taskManagementService.getTaskSaveModel()
                : null;
    }

    private mapEmployeeModel(): EmployeeLeaveModel {
        const employeeModel = new EmployeeLeaveModel();

        employeeModel.EmployeeId =
            this.employeeFormGroup.get('EmployeeId').value;
        employeeModel.Name = this.employeeFormGroup.get('Name').value;
        employeeModel.Surname = this.employeeFormGroup.get('Surname').value;
        employeeModel.EmployeeRef =
            this.employeeFormGroup.get('EmployeeRef').value;
        employeeModel.Nic = this.employeeFormGroup.get('Nic').value;
        employeeModel.Ss = this.employeeFormGroup.get('Ss').value;

        employeeModel.HiringDate =
            this.employeeFormGroup.get('HiringDate').value === null
                ? null
                : DateHelper.getUTCDateString(
                      DateHelper.getNativeDate(
                          this.employeeFormGroup.get('HiringDate').value
                      )
                  );

        employeeModel.ContractEndDate =
            this.employeeFormGroup.get('ContractEndDate').value === null
                ? null
                : DateHelper.getUTCDateString(
                      DateHelper.getNativeDate(
                          this.employeeFormGroup.get('ContractEndDate').value
                      )
                  );

        employeeModel.Gender = this.employeeFormGroup.get('Gender').value;

        employeeModel.BirthDate =
            this.employeeFormGroup.get('BirthDate').value === null
                ? null
                : DateHelper.getUTCDateString(
                      DateHelper.getNativeDate(
                          this.employeeFormGroup.get('BirthDate').value
                      )
                  );

        employeeModel.Age = this.employeeFormGroup.get('Age').value;
        employeeModel.Urgent = this.employeeFormGroup.get('Urgent').value;
        employeeModel.WorkingHourPercent =
            this.employeeFormGroup.get('WorkingHourPercent').value;
        employeeModel.Position = this.employeeFormGroup.get('Position').value;
        employeeModel.CompanyId = this.employeeFormGroup.get('CompanyId').value;

        employeeModel.Directions =
            this.employeeFormGroup.get('Directions').value;

        return employeeModel;
    }

    private mapLeaveModel(): LeaveModel {
        const leaveModel = new LeaveModel();

        leaveModel.leaveId = this.leaveForm.get('leaveId').value;
        leaveModel.leaveDate = DateHelper.getUTCDateString(
            DateHelper.getNativeDate(this.leaveForm.get('leaveDate').value)
        );

        leaveModel.dischargeDate =
            this.leaveForm.get('dischargeDate').value === null
                ? null
                : DateHelper.getUTCDateString(
                      DateHelper.getNativeDate(
                          this.leaveForm.get('dischargeDate').value
                      )
                  );

        leaveModel.leaveCommunication =
            this.leaveForm.get('leaveCommunication').value === null
                ? null
                : DateHelper.getUTCDateString(
                      DateHelper.getNativeDate(
                          this.leaveForm.get('leaveCommunication').value
                      )
                  );

        leaveModel.dischargeCommunication =
            this.leaveForm.get('dischargeCommunication').value === null
                ? null
                : DateHelper.getUTCDateString(
                      DateHelper.getNativeDate(
                          this.leaveForm.get('dischargeCommunication').value
                      )
                  );

        leaveModel.leaveType = this.leaveForm.get('leaveType').value;
        leaveModel.relapse = this.leaveForm.get('relapse').value;
        leaveModel.noPhone = this.leaveForm.get('noPhone').value;
        leaveModel.noAddress = this.leaveForm.get('noAddress').value;
        leaveModel.leaveRef = this.leaveForm.get('leaveRef').value;
        leaveModel.accidentPlace = this.leaveForm.get('accidentPlace').value;
        leaveModel.accidentLocation =
            this.leaveForm.get('accidentLocation').value;
        leaveModel.medicoId = this.leaveForm.get('medicoId').value;
        leaveModel.comments = this.leaveForm.get('comments').value;

        return leaveModel;
    }

    public isNewLeaveFormValid(): boolean {
        this.leaveForm.updateValueAndValidity();
        this.validateAllFormFields(this.leaveForm);
        let isValid = this.leaveForm.valid;

        const permissions = Utils.decrypt(localStorage.getItem('permissionIds')).split(',');

        if (permissions.includes('525')) {
            this.removeUnusedValidations();
            this.employeeFormGroup.updateValueAndValidity();
            this.validateAllFormFields(this.employeeFormGroup);
            isValid = isValid && this.employeeFormGroup.valid;
        }

        if (this.isOpenLeave && this.isManagedLeave) {
            this.taskManagementService.TaskManagementForm.updateValueAndValidity();
            this.validateAllFormFields(
                this.taskManagementService.TaskManagementForm
            );
            isValid =
                isValid && this.taskManagementService.TaskManagementForm.valid;
        }

        return isValid;
    }

    public createNewLeave(): Observable<RestResponse> {
        this.mapNewLeaveFormToModel();
        const url = `${API.Leave.createNewLeave}`;
        return this.apiService.post(url, this.newLeaveModel);
    }

    private validateAllFormFields(formGroup: UntypedFormGroup): void {
        Object.keys(formGroup.controls).forEach((field) => {
            const control = formGroup.get(field);
            if (control instanceof UntypedFormControl) {
                control.markAsTouched({ onlySelf: true });
            } else if (control instanceof UntypedFormGroup) {
                this.validateAllFormFields(control);
            }
        });
    }

    public async checkDateConflicts(): Promise<boolean> {
        let isConflict = false;

        const leaveDate: Moment = this.leaveForm.get('leaveDate').value;
        const dischargeDate: Moment = this.leaveForm.get('dischargeDate').value;

        if (!leaveDate || !this.employeeId) {
            return isConflict;
        }

        const res = await this.leaveService
            .GetLeaveListForValidations(String(this.employeeId))
            .toPromise();

        if (res.code === 0) {
            const leaveList: Array<any> = res.result;

            // CHECK IF HAS SAME LEAVE DATE
            let index = leaveList.findIndex(
                (e) =>
                    moment(e.Fbaja).format('DD-MM-YYYY') ===
                    leaveDate.format('DD-MM-YYYY')
            );
            isConflict = isConflict || index > -1;

            // CHECK IF LEAVE DATE IS IN BETWEEN ANOTHER CLOSED LEAVE
            index = leaveList.findIndex((e) =>
                leaveDate.isBetween(moment(e.Fbaja), moment(e.Falta))
            );
            isConflict = isConflict || index > -1;

            if (!dischargeDate) {
                // CHECK IF THERE'S ANOTHER OPEN LEAVE
                index = leaveList.findIndex((e) => e.Fbaja && !e.Falta);
                isConflict = isConflict || index > -1;

                // CHECK IF LEAVE DATE IS BEFORE ALL CLOSED LEAVES
                index = leaveList.findIndex((e) =>
                    leaveDate.isBefore(moment(e.Falta))
                );
                isConflict = isConflict || index > -1;
            } else {
                // CHECK IF SAME DISCHARGE DATE
                index = leaveList.findIndex(
                    (e) =>
                        moment(e.Falta).format('DD-MM-YYYY') ===
                        dischargeDate.format('DD-MM-YYYY')
                );
                isConflict = isConflict || index > -1;

                // CHECK IF DISCHARGE DATE IS IN BETWEEN ANOTHER CLOSED LEAVE
                index = leaveList.findIndex((e) =>
                    dischargeDate.isBetween(moment(e.Fbaja), moment(e.Falta))
                );
                isConflict = isConflict || index > -1;

                // CHECK IF OTHER LEAVE DATES ARE IN BETWEEN THE GIVEN LEAVE DATE AND DISCHARGE DATE
                index = leaveList.findIndex((e) =>
                    moment(e.Fbaja).isBetween(leaveDate, dischargeDate)
                );
                isConflict = isConflict || index > -1;

                // CHECK IF OTHER DISCHARGE DATES ARE IN BETWEEN THE GIVEN LEAVE DATE AND DISCHARGE DATE
                index = leaveList.findIndex((e) =>
                    moment(e.Falta).isBetween(leaveDate, dischargeDate)
                );
                isConflict = isConflict || index > -1;

                // CHECK IF DATES ARE AFTER OPEN LEAVE
                index = leaveList.findIndex(
                    (e) =>
                        e.Fbaja &&
                        !e.Falta &&
                        (leaveDate.isAfter(moment(e.Fbaja)) ||
                            dischargeDate.isAfter(moment(e.Fbaja)))
                );
                isConflict = isConflict || index > -1;
            }
        }

        return isConflict;
    }

    public resertLeaveDetails(): void {
        this.leaveValidatorService.validationForm = null;

        this.newLeaveModel = null;
        this.isOpenLeave = true;
        this.isManagedLeave = false;
        this.isLeaveInTiposBaja = false;
        this.isLeaveInTiposSinGestion = true;

        if (this.employeeFormGroup) {
            this.employeeFormGroup.reset();
        }

        if (this.leaveForm) {
            this.leaveForm.reset();
        }

        if (this.taskManagementService.TaskManagementForm) {
            this.taskManagementService.TaskManagementForm.reset();
        }
    }

    private removeUnusedValidations(): void {
        const age = this.employeeFormGroup.get('Age');
        if (!age.value) {
            age.setErrors(null);
        }

        const postalCode = this.employeeFormGroup.get('Directions.PostalCode');
        if (!postalCode.value) {
            postalCode.setErrors(null);
        }

        const postalCode2 = this.employeeFormGroup.get(
            'Directions.PostalCode2'
        );
        if (postalCode2 && !postalCode2.value) {
            postalCode2.setErrors(null);
        }
    }

    /**
     * Run the custom leave creation validators and return an error message if an error exists
     * otherwise return null
     */
    public async runLeaveCreationValidators(): Promise<string> {
        return new Promise<string>((resolve) => {
            if (
                this.leaveCreationValidators &&
                this.leaveCreationValidators.length > 0
            ) {
                this.leaveCreationValidators.forEach((validator) => {
                    const msg = validator(this.leaveForm);

                    if (msg) {
                        resolve(msg);
                    }
                });
            }

            resolve(null);
        });
    }

    /**
     * Check if leave date is greater than discharge date. If so, return an error message
     * otherwise, return null.
     */
    private leaveDischargeDateGTLeaveDateValidator(form: UntypedFormGroup): string {
        const leaveDate = form.get('leaveDate').value as Moment;
        const dischargeDate = form.get('dischargeDate').value as Moment;

        return dischargeDate &&
            leaveDate &&
            dischargeDate.isBefore(leaveDate, 'day')
            ? 'La fecha de alta debe ser superior o igual que la fecha de baja'
            : null;
    }
}
